import React from 'react';
import { useTheme } from '../theme';
import { huntBarSurface } from './MapUIComponents';

/** Linjeikon (samma stil som jaktmenyn). */
export function LineIcon({ d, size = 22 }: { d: React.ReactNode; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>{d}</svg>
  );
}

export const HUNT_ICONS = {
  play: <><circle cx="12" cy="12" r="9" /><path d="M10 8.5v7l5.5-3.5z" /></>,
  join: <><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="m10 17 5-5-5-5M15 12H3" /></>,
  plan: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /><path d="m9 15 2 2 4-4" /></>,
  open: <><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></>,
  mark: <><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>,
  back: <path d="M15 18l-6-6 6-6" />,
};

/**
 * Jaktknappen på kartan (helskärm, ingen jakt igång): frostad oval som jaktmenyn, orange kant för att
 * den är det primära valet (UX3), linjeikon i stället för fylld orange bakgrund.
 */
export function HuntFloatingButton({ onClick, label = 'Jakt' }: { onClick: () => void; label?: string }) {
  const T = useTheme();
  return (
    <button type="button" onClick={onClick} title="Jakt — anslut, starta eller planera" style={{
      ...huntBarSurface(T.bg, T.border), border: `1.5px solid ${T.amber}`,
      pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      height: 52, padding: '0 28px', minWidth: 160, cursor: 'pointer', fontFamily: 'inherit',
      color: T.text, fontWeight: 700, fontSize: 15, letterSpacing: '0.01em', WebkitTapHighlightColor: 'transparent',
    }}>
      <span style={{ color: T.amber, display: 'flex' }}><LineIcon d={HUNT_ICONS.play} /></span>
      {label}
    </button>
  );
}

export interface HuntSheetAction {
  key: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  /** Primärt val: orange kant (ingen fylld bakgrund). */
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

/** En rad i jaktpopupen: rundad pill, linjeikon, orange kant när den är primär eller vald. */
export function HuntSheetRow({ icon, label, hint, primary, disabled, onClick, trailing }: {
  icon: React.ReactNode; label: string; hint?: string; primary?: boolean; disabled?: boolean; onClick: () => void; trailing?: React.ReactNode;
}) {
  const T = useTheme();
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 52, padding: '6px 16px', boxSizing: 'border-box',
      borderRadius: 999, border: `1.5px solid ${primary ? T.amber : 'transparent'}`,
      background: primary ? `color-mix(in srgb, ${T.amber} 12%, transparent)` : 'transparent',
      color: disabled ? T.textMuted : T.text, opacity: disabled ? 0.6 : 1,
      cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', textAlign: 'left', WebkitTapHighlightColor: 'transparent',
    }}>
      <span style={{ color: primary ? T.amber : T.text, display: 'flex' }}><LineIcon d={icon} /></span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        {hint && <span style={{ fontSize: 12, color: T.textSub, fontWeight: 400 }}>{hint}</span>}
      </span>
      {trailing}
    </button>
  );
}

/**
 * Popupen från jaktknappen (UX3/UX4): flytande frostad ruta ovanför knappen, samma stil som kameraväljaren.
 * Steg 1 (bara om man har rätt till fler än en mark): välj mark. Steg 2: åtgärderna för den marken.
 */
export function HuntStartSheet({ title, text, marks, chosenMarkId, onChooseMark, actions, onClose, busy }: {
  title: string;
  text?: string;
  /** Markerna man kan starta/planera jakt på; visas som steg 1 när det finns fler än en och ingen är vald. */
  marks?: { id: string; name: string; current?: boolean }[];
  chosenMarkId: string | null;
  onChooseMark: (id: string | null) => void;
  actions: HuntSheetAction[];
  onClose: () => void;
  busy?: boolean;
}) {
  const T = useTheme();
  const pickMark = !!marks && marks.length > 1 && !chosenMarkId;
  const chosen = marks?.find(m => m.id === chosenMarkId);
  return (
    <div role="dialog" aria-modal aria-label={title} onClick={() => { if (!busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 10020, background: 'rgba(0,0,0,0.28)' }}>
      <div onClick={e => e.stopPropagation()} style={{
        ...huntBarSurface(T.bg, T.border), borderRadius: 26,
        position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 'calc(86px + env(safe-area-inset-bottom, 0px))',
        width: 'min(360px, calc(100vw - 20px))', maxHeight: 'calc(100% - 140px - env(safe-area-inset-top, 0px))', overflowY: 'auto',
        boxSizing: 'border-box', padding: 6, color: T.text, display: 'flex', flexDirection: 'column', gap: 2,
      }}>
        <div style={{ padding: '10px 16px 6px' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{pickMark ? 'Välj mark' : title}</div>
          {pickMark
            ? <div style={{ fontSize: 12.5, color: T.textSub, marginTop: 2, lineHeight: 1.45 }}>Vilken mark vill du starta eller planera jakt på?</div>
            : text && <div style={{ fontSize: 12.5, color: T.textSub, marginTop: 2, lineHeight: 1.45 }}>{text}</div>}
        </div>
        {pickMark ? marks!.map(m => (
          <HuntSheetRow key={m.id} icon={HUNT_ICONS.mark} label={m.name} hint={m.current ? 'Den här marken' : undefined}
            primary={m.current} onClick={() => onChooseMark(m.id)} />
        )) : <>
          {marks && marks.length > 1 && chosen && (
            <HuntSheetRow icon={HUNT_ICONS.mark} label={chosen.name} hint="Byt mark" disabled={busy} onClick={() => onChooseMark(null)}
              trailing={<span style={{ color: T.textSub, display: 'flex', transform: 'rotate(180deg)' }}><LineIcon d={HUNT_ICONS.back} size={18} /></span>} />
          )}
          {actions.map(a => (
            <HuntSheetRow key={a.key} icon={a.icon} label={a.label} hint={a.hint} primary={a.primary} disabled={a.disabled || busy} onClick={a.onClick} />
          ))}
        </>}
        <button type="button" onClick={onClose} disabled={busy} style={{
          margin: '4px 0 2px', minHeight: 44, borderRadius: 999, border: 'none', background: 'transparent',
          color: T.textSub, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}>Stäng</button>
      </div>
    </div>
  );
}
